import { resetPipeState } from '~/composables/useVideoPipeGovernor';
import {
	SHORTS_BUFFER_SUSTAINED_EVENT,
	SHORTS_FIRST_FRAME_EVENT
} from '~/utils/shortsEvents';
import { waitForFirstSignal } from '~/utils/waitForFirstSignal';

/**
 * Coisas de outros imoveis esperam o video ativo ter buffer folgado. Gesto
 * abre na hora; o fallback evita gate preso.
 */
const NEIGHBOR_FALLBACK_MS = 20000;

const neighborsRevealed = ref(false);
let hasSeenFirstFrame = false;
if (import.meta.client) {
	window.addEventListener(
		SHORTS_FIRST_FRAME_EVENT,
		() => {
			hasSeenFirstFrame = true;
		},
		{ once: true }
	);
}
let neighborsArmed = false;
let neighborGateCancel: (() => void) | null = null;

// keydown/wheel inclusos: so pointerdown deixava o usuario de desktop
// esperando o fallback inteiro.
const GATE_OPENING_GESTURES = ['pointerdown', 'keydown', 'wheel'] as const;

/**
 * Arma um gate; devolve um cancelador para descartar o ciclo antes dele abrir.
 * O fallback so dispara com `hasSeenFirstFrame`: soltar vizinhos as cegas,
 * antes do ativo ter dado o primeiro frame, tira banda de quem o usuario
 * esta esperando ver. Sinal real e gesto continuam abrindo na hora — sao
 * intencao explicita do usuario, nao um timeout cego.
 */
function armGate(
	gate: Ref<boolean>,
	eventName: string,
	fallbackMs: number,
	onOpen: () => void
): () => void {
	const { promise, cancel } = waitForFirstSignal(
		[eventName],
		fallbackMs,
		GATE_OPENING_GESTURES,
		() => hasSeenFirstFrame
	);
	promise.then(() => {
		gate.value = true;
		onOpen();
	});
	return cancel;
}

export function useShortsNeighborGate(): Ref<boolean> {
	if (import.meta.client && !neighborsRevealed.value && !neighborsArmed) {
		neighborsArmed = true;
		neighborGateCancel = armGate(
			neighborsRevealed,
			SHORTS_BUFFER_SUSTAINED_EVENT,
			NEIGHBOR_FALLBACK_MS,
			() => {
				neighborsArmed = false;
				neighborGateCancel = null;
			}
		);
	}
	return neighborsRevealed;
}

/**
 * Re-fecha o gate de vizinhos para uma nova sessao de feed: o video clicado
 * ganha o cano de novo ate ter buffer folgado. Cancela o ciclo anterior antes
 * de rearmar — sem isso, uma sessao reaberta antes do fallback do ciclo
 * anterior expirar ficava presa a um timer/listener que ja nao correspondia
 * a uma janela de 20s a partir de agora.
 */
export function rearmNeighborGate(): void {
	if (import.meta.server) return;
	neighborGateCancel?.();
	neighborGateCancel = null;
	neighborsArmed = false;
	neighborsRevealed.value = false;
	resetPipeState();
	useShortsNeighborGate();
}

/**
 * Espera a janela de vizinhos abrir antes de gastar banda com outros imoveis.
 * Reusa o proprio gate de vizinhos (mesmo evento SUSTAINED, mesmo fallback)
 * em vez de reimplementar a espera com o evento HEALTHY — os dois nunca
 * coincidiam: PropertyReelsFeedItem so monta vizinhos no SUSTAINED, entao
 * esperar o HEALTHY liberava chamadas de historico/feed segundos antes do
 * gate que elas deveriam respeitar.
 */
export function waitForNeighborWindow(): Promise<void> {
	if (import.meta.server) return Promise.resolve();
	const revealed = useShortsNeighborGate();
	if (revealed.value) return Promise.resolve();
	return new Promise(resolve => {
		const stop = watch(revealed, isRevealed => {
			if (!isRevealed) return;
			stop();
			resolve();
		});
	});
}
